import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import getAPIbaseURL from "../../utils/helpers";
import i18n from "../../i18n";

const API_URL = `${getAPIbaseURL()}/presences`;

export const startPause = createAsyncThunk(
  "pauses/startPause",
  async ({ presenceId, reason, start_location, start, end }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token")?.replace(/^"|"$/g, "");
      const body = {
        reason,
        start_location,
        start,
        end,
      };
      const response = await axios.post(`${API_URL}/${presenceId}/pauses/start`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.pausesSlice.startPauseError"));
    }
  }
);

export const endPausePause = createAsyncThunk(
  "pauses/endPause",
  async (payload, thunkAPI) => {
    try {
      const { pauseId, end_location, end_accuracy, reason = "" } = payload;
      const token = localStorage.getItem("token")?.replace(/^"|"$/g, "");
      const body = {
        reason,
        end_location,
        end_accuracy,
      };
      const response = await axios.post(
        `${API_URL}/pauses/${pauseId}/end`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.pausesSlice.endPauseError"));
    }
  }
);

export const fetchPauses = createAsyncThunk(
  "pauses/fetchPauses",
  async (presenceId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token")?.replace(/^"|"$/g, "");
      const response = await axios.get(`${API_URL}/${presenceId}/pauses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { presenceId, pauses: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.pausesSlice.fetchPausesError"));
    }
  }
);

export const updatePauseTimes = createAsyncThunk(
  "pauses/updatePauseTimes",
  async ({ pauseId, timeData }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token")?.replace(/^"|"$/g, "");
      const response = await axios.put(`${API_URL}/pauses/${pauseId}/times`, timeData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      thunkAPI.dispatch(fetchPauses(response.data.presence_id));
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.pausesSlice.updatePauseTimesError"));
    }
  }
);

export const deletePause = createAsyncThunk(
  "pauses/deletePause",
  async (pauseId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token")?.replace(/^"|"$/g, "");
      await axios.delete(`${API_URL}/pauses/${pauseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return pauseId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.pausesSlice.deletePauseError"));
    }
  }
);

export const fetchPauseReasons = createAsyncThunk(
  "pauses/fetchPauseReasons",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token")?.replace(/^"|"$/g, "");
      const response = await axios.get(`${getAPIbaseURL()}/time-balances/pause-reasons`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data; 
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || i18n.t("slices.pausesSlice.fetchPauseReasonsError"));
    }
  }
);

const pausesSlice = createSlice({
  name: "pauses",
  initialState: {
    byPresence: {},
    reasons: [],
    isLoading: false,
    error: null,
    message: null,
  },
  reducers: {
    clearPauseMessage: (state) => {
      state.message = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startPause.fulfilled, (state, action) => {
        const pause = action.payload;
        if (!state.byPresence[pause.presence_id]) state.byPresence[pause.presence_id] = [];
        state.byPresence[pause.presence_id].push(pause);
        state.message = i18n.t("slices.pausesSlice.pauseStartedSuccess");
      })
      .addCase(endPausePause.fulfilled, (state, action) => {
        const updated = action.payload;
        const list = state.byPresence[updated.presence_id];
        if (list) {
          const index = list.findIndex((p) => p.id === updated.id);
          if (index !== -1) list[index] = updated;
        }
        state.message = i18n.t("slices.pausesSlice.pauseEndedSuccess");
      })
      .addCase(fetchPauses.fulfilled, (state, action) => {
        state.byPresence[action.payload.presenceId] = action.payload.pauses;
      })
      .addCase(updatePauseTimes.fulfilled, (state, action) => {
        const updated = action.payload;
        const list = state.byPresence[updated.presence_id];
        if (list) {
          const index = list.findIndex((p) => p.id === updated.id);
          if (index !== -1) list[index] = updated;
        }
        state.message = i18n.t("slices.pausesSlice.pauseTimesUpdatedSuccess");
      })
      .addCase(deletePause.fulfilled, (state, action) => {
        for (const key in state.byPresence) {
          state.byPresence[key] = state.byPresence[key].filter((p) => p.id !== action.payload);
        }
        state.message = i18n.t("slices.pausesSlice.pauseDeletedSuccess");
      })
      .addCase(fetchPauseReasons.fulfilled, (state, action) => {
          state.reasons = action.payload;
      })
      .addMatcher(
        (action) => action.type.startsWith("pauses/") && action.type.endsWith("/rejected"),
        (state, action) => {
          state.error = action.payload;
          state.isLoading = false;
        }
      )
      .addMatcher(
        (action) => action.type.startsWith("pauses/") && action.type.endsWith("/pending"),
        (state) => {
          state.error = null;
          state.isLoading = true;
        }
      )
      .addMatcher(
        (action) => action.type.startsWith("pauses/") && action.type.endsWith("/fulfilled"),
        (state) => {
          state.error = null;
          state.isLoading = false;
        }
      );
  },
});

export const { clearPauseMessage } = pausesSlice.actions;
export default pausesSlice.reducer;